import React, { useState, useEffect } from "react";
import { useUser } from "../contexts/UserContext";
import { uploadAndParseResume } from "../services/resumeService";
import { customToast } from '../utils/toastUtils';
import AtsScoreResult from "./AtsScoreResult";
import "./FileUploadModel.css";

const FileUploadModel = ({ show, onClose, onFileUpload, type, title = "Upload Resume", onAtsCheckComplete, onResumeUploadComplete }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [atsResult, setAtsResult] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [useCache, setUseCache] = useState(true); // Cache control for testing
  const [importStage, setImportStage] = useState(null); // 'importing' | 'extracting' | 'almost' | null
  
  const { userData } = useUser();

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (show) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [show]);

  if (!show) return null;

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    validateAndSetFile(file);
  };

  const validateAndSetFile = (file) => {
    if (!file) return;
    
    // Check file type
    if (!(file.type === "application/pdf" || file.name.endsWith('.docx'))) {
      setUploadError("Please select a PDF or DOCX file");
      return;
    }
    
    // Check file size (2MB = 2 * 1024 * 1024 bytes)
    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      setUploadError("File size must be less than 2MB");
      return;
    }
    
    setSelectedFile(file);
    setUploadError(null);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const droppedFile = e.dataTransfer.files[0];
    validateAndSetFile(droppedFile);
  };

  const handleSubmit = async () => {
    if (selectedFile) {
      // Check if this is an ATS check upload
      if (type === 'ats-check') {
        setIsUploading(true);
        setUploadError(null);

        try {
          // For now, navigate directly to ATS page with the file
          // In the future, you can uncomment the API call below
          
          // Simulate a small delay for UX
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Call the callback to navigate to ATS Score page
          if (onAtsCheckComplete) {
            onAtsCheckComplete(selectedFile, useCache); // Pass useCache value
          }
          
          // Reset state
          setSelectedFile(null);
          
          /* 
          // Future API call (uncomment when backend is ready):
          const formData = new FormData();
          formData.append('resume', selectedFile);

          const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:5000';
          const response = await fetch(`${backendUrl}/api/ats-score/upload`, {
            method: 'POST',
            body: formData,
          });

          if (!response.ok) {
            throw new Error('Failed to analyze resume');
          }

          const data = await response.json();
          setAtsResult(data);
          setShowResult(true);
          setSelectedFile(null);
          */
        } catch (error) {
          console.error('Error uploading resume:', error);
          setUploadError(error.message || 'Failed to analyze resume. Please try again.');
        } finally {
          setIsUploading(false);
        }
      } else if (type === 'resume-upload') {
        // Resume import flow: show animation immediately, upload in background
        setIsUploading(true);
        setUploadError(null);
        setImportStage('importing'); // Show animation right away

        try {
          // Call the upload and parse service
          const result = await uploadAndParseResume(selectedFile, userData.uid);
          
          // Continue animated stages after parsing completes
          setIsUploading(false);
          setImportStage('extracting');
          await new Promise(resolve => setTimeout(resolve, 500));
          setImportStage('almost');
          await new Promise(resolve => setTimeout(resolve, 500));
          
          customToast.success(`Resume imported! Found ${result.sectionsFound.experience} work experiences, ${result.sectionsFound.education} education entries`);
          
          setImportStage(null);
          
          // Call the callback to navigate to templates
          if (onResumeUploadComplete) {
            onResumeUploadComplete(result.parsedDataId);
          }
          
          // Reset state
          setSelectedFile(null);
          
        } catch (error) {
          console.error('Error uploading resume:', error);
          setImportStage(null);
          setUploadError(error.message || 'Failed to upload and parse resume. Please try again.');
          customToast.error(error.message || 'Failed to import resume');
        } finally {
          setIsUploading(false);
        }
      } else {
        // Regular file upload
        if (onFileUpload) {
          onFileUpload(selectedFile);
        }
        setSelectedFile(null);
        onClose();
      }
    }
  };

  const handleCloseResult = () => {
    setShowResult(false);
    setAtsResult(null);
    onClose();
  };

  const handleCloseModal = () => {
    setSelectedFile(null);
    setUploadError(null);
    setIsUploading(false);
    onClose();
  };

  const resetFile = () => {
    setSelectedFile(null);
  };

  // Dynamic title and description based on type
  const modalTitle = type === 'resume-upload'
    ? 'Import Your Resume'
    : (type === 'ats-check' ? 'Upload Resume for ATS Check' : title);
    
  const modalDescription = type === 'resume-upload'
    ? 'Upload your existing resume to extract data and use it with professional templates'
    : 'Analyze your resume for ATS compatibility';

  // Show import animation screen
  if (importStage) {
    return (
      <div className="file-modal-overlay">
        <div className="file-modal import-animation-modal">
          <div className="import-animation-container">
            <div className="import-document-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="64" height="64">
                <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6z" fill="#1A8063"/>
                <path d="M14 2v6h6" fill="#14665a"/>
                <path d="M9 13h6M9 16h4" stroke="#fff" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              <div className="import-pulse-ring"></div>
            </div>
            
            <div className="import-stages">
              <div className={`import-stage ${importStage === 'importing' ? 'active' : importStage === 'extracting' || importStage === 'almost' ? 'done' : ''}`}>
                <span className="stage-dot"></span>
                <span className="stage-text">Importing your resume...</span>
              </div>
              <div className={`import-stage ${importStage === 'extracting' ? 'active' : importStage === 'almost' ? 'done' : ''}`}>
                <span className="stage-dot"></span>
                <span className="stage-text">Extracting content...</span>
              </div>
              <div className={`import-stage ${importStage === 'almost' ? 'active' : ''}`}>
                <span className="stage-dot"></span>
                <span className="stage-text">Almost there...</span>
              </div>
            </div>

            <div className="import-progress-bar">
              <div className={`import-progress-fill stage-${importStage}`}></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="file-modal-overlay">
        <div className="file-modal">
          <h2>{modalTitle}</h2>
          
          {/* Description for resume upload */}
          {type === 'resume-upload' && (
            <p className="modal-description" style={{ 
              marginBottom: '1rem', 
              color: '#64748b', 
              fontSize: '0.9rem',
              textAlign: 'center'
            }}>
              {modalDescription}
            </p>
          )}
          
          {uploadError && (
            <div className="upload-error">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="20" height="20" className="error-icon">
                <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" fill="#ef4444"/>
              </svg>
              {uploadError}
            </div>
          )}
          
          {/* File Size Warning */}
          <div className="file-size-warning">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="16" height="16" className="info-icon">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="#3b82f6"/>
            </svg>
            <span>File size must be less than 2MB</span>
          </div>
          
          <div 
            className={`file-drop-zone ${isDragOver ? 'drag-active' : ''}`}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleFileDrop}
          >
            {!selectedFile ? (
              <>
                <div className="upload-icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="48" height="48">
                    <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" fill="#94a3b8"/>
                  </svg>
                </div>
                <p className="drop-text">Drag & drop your resume here</p>
                <p className="file-info">Supports PDF and DOCX files</p>
                <input
                  type="file"
                  id="fileInput"
                  accept=".pdf,.docx"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
                <label htmlFor="fileInput" className="browse-btn">
                  Browse Files
                </label>
              </>
            ) : (
              <div className="selected-file">
                <div className="file-icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="40" height="40">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="#10b981"/>
                  </svg>
                </div>
                <p className="file-name">{selectedFile.name}</p>
                <p className="file-size">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                <button onClick={resetFile} className="remove-file-btn">
                  Remove
                </button>
              </div>
            )}
          </div>

          <div className="file-modal-actions">
            <button onClick={handleCloseModal} className="cancel-btn" disabled={isUploading}>
              Cancel
            </button>
            <button 
              onClick={handleSubmit} 
              className="upload-submit-btn"
              disabled={!selectedFile || isUploading}
            >
              {isUploading ? (
                <span className="loading-text">
                  <span className="spinner"></span>
                  Analyzing...
                </span>
              ) : (
                type === 'ats-check' ? 'Check ATS Score' : 'Upload Resume'
              )}
            </button>
          </div>
        </div>
      </div>

      {/* ATS Score Result Modal */}
      <AtsScoreResult 
        show={showResult}
        onClose={handleCloseResult}
        result={atsResult}
      />
    </>
  );
};

export default FileUploadModel;